import Button from 'devextreme-react/button';
import { Item } from 'devextreme-react/data-grid';
import FileUploader from 'devextreme-react/file-uploader';
import Popup from 'devextreme-react/popup';
import { useEffect, useState, type ReactNode } from 'react';
import type { OpsGridToolbarMenuItem } from '../opsDataGrid/OpsGridToolbar';
import type {
  PatientTrackingCsvImportConfig,
  PatientTrackingCsvImportDefinition,
  PatientTrackingImportResult
} from './patientTrackingTypes';

const DEFAULT_IMPORT_INTRO =
  'Each CSV row must include a pharmacy value using the pharmacy name or GUID. Add pharmacies in Our Pharmacies before importing.';

export function createPatientTrackingCsvImportConfig(
  definition: PatientTrackingCsvImportDefinition,
  toasts?: PatientTrackingCsvImportConfig['toasts']
): PatientTrackingCsvImportConfig {
  return { ...definition, toasts };
}

export interface UsePatientTrackingCsvImportOptions {
  config?: PatientTrackingCsvImportConfig;
  disabled?: boolean;
  onImported?: () => void;
}

export interface PatientTrackingCsvImportUi {
  uploadToolbarItem: ReactNode;
  uploadMenuItem: OpsGridToolbarMenuItem | null;
  importPopup: ReactNode;
}

/**
 * Toolbar upload button + import popup. Render `importPopup` outside the DataGrid toolbar
 * (sibling to the grid) so DevExtreme mounts the popup correctly.
 */
export function usePatientTrackingCsvImport({
  config,
  disabled,
  onImported
}: UsePatientTrackingCsvImportOptions): PatientTrackingCsvImportUi {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  if (!config || !onImported) {
    return { uploadToolbarItem: null, uploadMenuItem: null, importPopup: null };
  }

  const openImport = () => setOpen(true);

  const uploadToolbarItem = (
    <Item
      location="after"
      widget="dxButton"
      options={{
        icon: 'upload',
        hint: `Import ${config.title} CSV`,
        stylingMode: 'outlined',
        disabled: disabled || importing,
        onClick: openImport
      }}
    />
  );

  const uploadMenuItem: OpsGridToolbarMenuItem = {
    id: 'import-csv',
    text: `Import ${config.title} CSV`,
    icon: 'upload',
    disabled: disabled || importing,
    onClick: openImport
  };

  const importPopup = (
    <PatientTrackingCsvImportPopup
      config={config}
      visible={open}
      importing={importing}
      setImporting={setImporting}
      onClose={() => {
        if (!importing) setOpen(false);
      }}
      onImported={onImported}
    />
  );

  return { uploadToolbarItem, uploadMenuItem, importPopup };
}

/** @deprecated Use usePatientTrackingCsvImport and render importPopup outside the grid toolbar. */
export function PatientTrackingCsvImportToolbarButton({
  config,
  disabled,
  onImported
}: {
  config: PatientTrackingCsvImportConfig;
  disabled?: boolean;
  onImported: () => void;
}) {
  const { uploadToolbarItem } = usePatientTrackingCsvImport({ config, disabled, onImported });
  return <>{uploadToolbarItem}</>;
}

export interface PatientTrackingCsvImportPopupProps {
  config: PatientTrackingCsvImportConfig;
  visible: boolean;
  importing: boolean;
  setImporting: (value: boolean) => void;
  onClose: () => void;
  onImported: () => void;
}

export function PatientTrackingCsvImportPopup({
  config,
  visible,
  importing,
  setImporting,
  onClose,
  onImported
}: PatientTrackingCsvImportPopupProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<PatientTrackingImportResult | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const intro = config.intro ?? DEFAULT_IMPORT_INTRO;
  const selectedFileName = files[0]?.name ?? null;

  useEffect(() => {
    if (!visible) return;
    setFiles([]);
    setResult(null);
    setUploaderKey((value) => value + 1);
  }, [visible]);

  async function downloadTemplate() {
    setDownloadingTemplate(true);
    try {
      if (config.downloadTemplate) {
        await config.downloadTemplate();
        return;
      }
      const link = document.createElement('a');
      link.href = config.templateUrl;
      link.download = config.templateFileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      config.toasts?.showError(err instanceof Error ? err.message : 'Failed to download template.');
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function runImport() {
    if (files.length === 0) {
      config.toasts?.showError('Choose a CSV file to import.');
      return;
    }

    setImporting(true);
    setResult(null);
    try {
      const nextResult = await config.importCsv(files[0]);
      setResult(nextResult);
      setFiles([]);
      setUploaderKey((value) => value + 1);
      onImported();

      if (nextResult.inserted > 0) {
        config.toasts?.showSuccess(`Imported ${nextResult.inserted} row(s).`);
      } else if (nextResult.errors.length > 0) {
        config.toasts?.showError('No rows imported. Check the row errors below.');
      } else {
        config.toasts?.showError('No data rows found in the CSV. Add rows below the header and try again.');
      }
    } catch (err) {
      config.toasts?.showError(err instanceof Error ? err.message : 'CSV import failed.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <Popup
      visible={visible}
      title={`Import ${config.title}`}
      showTitle
      showCloseButton
      dragEnabled
      hideOnOutsideClick={!importing}
      width={520}
      maxHeight="90vh"
      height="auto"
      onHiding={onClose}
    >
      <div className="pt-csv-import">
        <div className="pt-csv-import__callout" role="note">
          <span className="pt-csv-import__callout-icon dx-icon-info" aria-hidden />
          <p>{intro}</p>
        </div>

        <section className="pt-csv-import__panel">
          <div className="pt-csv-import__panel-head">
            <span className="pt-csv-import__step-badge">1</span>
            <div>
              <h4 className="pt-csv-import__panel-title">Download template</h4>
              <p className="pt-csv-import__panel-desc">
                Use the example CSV as a starting point, then fill in your rows.
              </p>
            </div>
          </div>
          <Button
            text={downloadingTemplate ? 'Downloading…' : 'Download CSV template'}
            icon="download"
            stylingMode="outlined"
            width="100%"
            disabled={importing || downloadingTemplate}
            onClick={() => void downloadTemplate()}
          />
        </section>

        <section className="pt-csv-import__panel pt-csv-import__panel--upload">
          <div className="pt-csv-import__panel-head">
            <span className="pt-csv-import__step-badge">2</span>
            <div>
              <h4 className="pt-csv-import__panel-title">Upload completed file</h4>
              <p className="pt-csv-import__panel-desc">
                Choose a CSV file or drag it into the area below.
              </p>
            </div>
          </div>
          <div className="pt-csv-import__dropzone">
            <FileUploader
              key={uploaderKey}
              accept=".csv,text/csv"
              multiple={false}
              uploadMode="useForm"
              selectButtonText="Choose file"
              labelText=""
              onValueChanged={(e) => setFiles((e.value as File[]) ?? [])}
            />
            <p className="pt-csv-import__dropzone-hint">CSV files only · one file at a time</p>
            {selectedFileName ? (
              <p className="pt-csv-import__selected-file">
                <span className="dx-icon-doc" aria-hidden /> {selectedFileName}
              </p>
            ) : null}
          </div>
        </section>

        {result ? (
          <section
            className={`pt-csv-import__results${result.errors.length > 0 ? ' pt-csv-import__results--errors' : ''}`}
            aria-live="polite"
          >
            <div className="pt-csv-import__results-summary">
              <strong>Import finished</strong>
              <span>
                {result.inserted} imported · {result.skipped} skipped · {result.errors.length} error
                {result.errors.length === 1 ? '' : 's'}
              </span>
            </div>
            {result.errors.length > 0 ? (
              <div className="pt-csv-import__errors">
                {result.errors.slice(0, 50).map((err, index) => (
                  <div key={`${err.rowNumber}-${err.field}-${index}`} className="pt-csv-import__error-row">
                    <span className="pt-csv-import__error-meta">
                      Row {err.rowNumber || 'file'} · {err.field}
                    </span>
                    <span>{err.message}</span>
                  </div>
                ))}
                {result.errors.length > 50 ? (
                  <p className="pt-csv-import__errors-more">
                    Showing first 50 errors. Fix those and retry to see the next set.
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        <footer className="pt-csv-import__footer">
          <Button text="Cancel" stylingMode="text" disabled={importing} onClick={onClose} />
          <Button
            text={importing ? 'Importing…' : 'Import CSV'}
            type="default"
            icon="upload"
            disabled={importing || files.length === 0}
            onClick={() => void runImport()}
          />
        </footer>
      </div>
    </Popup>
  );
}
